/**
 * Offers to save a copied note as the example note of the selected template.
 *
 * After "Afrita", if the selected personal template has no example_note, the
 * caller shows ExampleNotePromptDialog. On "Já" the BFF suggests a copy with
 * names, kennitala, addresses etc. swapped for fakes, and the user reviews and
 * edits it before it is saved: the example goes into the prompt of every later
 * note from the template, so it must not carry this patient's details.
 * "Ekki sýna aftur" is stored in the user's features so it follows them
 * across devices.
 */

import { type ClinicalTemplate, fetchJson, isPersonalTemplate } from '@/components/templates/template-utils';
import { hasTemplatesWriteAccess, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useSettings } from '@/hooks/useSettings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

const DISMISSED_KEY = 'example_note_prompt_dismissed';

export interface ExampleNoteReview {
  /** Suggested text with PII replaced; the original note when detection failed. */
  text: string;
  /** Number of replaced spans per PII type. */
  replaced: Record<string, number>;
  /** Whether all detector stages ran successfully; manual review is always required. */
  complete: boolean;
  /** Set by the client when the detection request itself failed. */
  failed?: boolean;
}

export type ExampleNotePrompt = {
  /** Template + session the prompt was made for; it is only shown while both are still selected. */
  key: string;
  templateId: number;
  templateName: string;
  note: string;
} & ({ step: 'ask' | 'anonymizing' } | { step: 'review'; review: ExampleNoteReview });

interface Options {
  templateId: number | null | undefined;
  sessionId: string | null | undefined;
}

export const useExampleNotePrompt = ({ templateId, sessionId }: Options) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { features, isReady, setFeatures } = useSettings();
  const [state, setState] = useState<ExampleNotePrompt | null>(null);
  const queryClient = useQueryClient();
  const details = useQuery({
    queryKey: ['templates', 'detail', user?.id, templateId],
    queryFn: () => fetchJson<ClinicalTemplate>(`/api/templates/${templateId}`),
    enabled: false,
    retry: false,
  });
  const anonymize = useMutation({
    mutationFn: (note: string) =>
      fetchJson<ExampleNoteReview>('/api/templates/example-note/anonymize', {
        method: 'POST',
        body: JSON.stringify({ text: note }),
      }),
  });
  const save = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      fetchJson<ClinicalTemplate>(`/api/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ example_note: text }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
  const isSaving = save.isPending;
  // Ask at most once per template while the panel is mounted, so a "Nei" is not
  // repeated on every copy of the same session.
  const askedTemplateIds = useRef(new Set<number>());

  // A response that arrives after the template or session changed is dropped.
  const contextKey = `${templateId ?? ''}:${sessionId ?? ''}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  // Read after awaits: a dismissal made meanwhile must win over a late response.
  const dismissedRef = useRef(false);
  dismissedRef.current = dismissedRef.current || features?.[DISMISSED_KEY] === true;

  // A prompt belongs to the template + session it was made for: drop it when either changes,
  // so switching back does not bring an old prompt back.
  if (state !== null && state.key !== contextKey) setState(null);
  const prompt = state?.key === contextKey ? state : null;

  /**
   * `key` is the contextKey read before the copy started, so a template or
   * session switch during the clipboard write cannot pair this note with the new one.
   */
  const offerAfterCopy = async (
    template: Pick<ClinicalTemplate, 'id' | 'name' | 'scope' | 'is_personal'> | undefined,
    note: string,
    key: string
  ) => {
    if (!template || !isReady || !hasTemplatesWriteAccess(user) || dismissedRef.current || !note.trim()) return;
    if (key !== contextKeyRef.current) return;
    // Only personal templates can be edited by the user (org templates are read-only).
    if (!isPersonalTemplate(template) || askedTemplateIds.current.has(template.id)) return;
    askedTemplateIds.current.add(template.id);

    try {
      // The template list leaves out example_note, so read the full template.
      const { data: full, error } = await details.refetch();
      if (error || !full) throw new Error('Template unavailable');
      if (key !== contextKeyRef.current || dismissedRef.current || full.example_note?.trim()) return;
      setState({ key, templateId: template.id, templateName: full.name || template.name, note, step: 'ask' });
    } catch {
      // Let a later copy try again.
      askedTemplateIds.current.delete(template.id);
    }
  };

  const startReview = async () => {
    if (!prompt) return;
    setState({ ...prompt, step: 'anonymizing' });
    let review: ExampleNoteReview;
    try {
      review = await anonymize.mutateAsync(prompt.note);
    } catch {
      review = { text: prompt.note, replaced: {}, complete: false, failed: true };
    }
    // Only if the user is still waiting on this prompt (not closed, not switched away).
    setState((current) =>
      current?.key === prompt.key && current.step === 'anonymizing' ? { ...current, step: 'review', review } : current
    );
  };

  const close = (dontShowAgain: boolean, key = prompt?.key) => {
    if (dontShowAgain) {
      dismissedRef.current = true;
      setFeatures(
        { [DISMISSED_KEY]: true },
        {
          onError: () => {
            dismissedRef.current = false;
            toast({
              title: 'Ekki tókst að vista stillinguna',
              description: 'Spurningin getur birst aftur. Reyndu aftur síðar.',
              variant: 'destructive',
            });
          },
        }
      );
    }
    // Only clear the prompt this call is about, never a newer one.
    setState((current) => (current?.key === key ? null : current));
  };

  const saveAsExample = async (text: string, dontShowAgain: boolean) => {
    if (!prompt || !text.trim()) return;
    const { key } = prompt;
    try {
      await save.mutateAsync({ id: prompt.templateId, text });
      toast({
        title: 'Dæmi um nótu vistað',
        description: `Sniðmátið ${prompt.templateName} notar nú þessa nótu sem dæmi.`,
      });
      close(dontShowAgain, key);
    } catch {
      toast({
        title: 'Ekki tókst að vista dæmi um nótu',
        description: 'Reyndu aftur eða bættu því við á sniðmátasíðunni.',
        variant: 'destructive',
      });
    }
  };

  return { contextKey, prompt, isSaving, offerAfterCopy, startReview, saveAsExample, close };
};
